import { create, el, VNode } from "@dhx/ts-common/dom";
import { IHandlers } from "@dhx/ts-common/types";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { locate } from "@dhx/ts-common/html";
import { View } from "@dhx/ts-common/view";
import { DataCollection, DataEvents } from "@dhx/ts-data";
import { Uploader, UploaderEvents, IFileWrapper } from "@dhx/ts-vault";
import { Popup } from "@dhx/ts-popup";
import { getFormItemCss, getValidationMessage, isEmptyObj } from "../helper";
import locale from "../locales/en";
import { ValidationStatus, ISimpleVault, IBaseSimpleVaultEventHandlersMap, BaseElementEvent } from "../types";


export class SimpleVault extends View {
	public events: IEventSystem<UploaderEvents | DataEvents | BaseElementEvent | IBaseSimpleVaultEventHandlersMap | any>;
	public data: DataCollection<IFileWrapper>;
	protected _helper: Popup;
	private _uploader: Uploader;
	private _handlers: IHandlers;
	private _dragover: boolean;
	private _dragoverTimeout: any;

	constructor(container: HTMLElement | string, config: ISimpleVault) {
		super(container, config);

		this.events = new EventSystem<DataEvents | UploaderEvents>(this);
		this.data = new DataCollection({}, this.events);
		this._uploader = new Uploader({
			...config as any,
			autosend: false
		}, this.data, this.events);

		this.events.on(BaseElementEvent.change, () => {
			this.validate();
			this.paint();
		});

		this._handlers = {
			add: (e: Event) => {
				if (this.config.disabled) {
					return;
				}
				e.preventDefault();
				this._uploader.selectFile();
			},
			remove: (e: Event) => {
				if (this.config.disabled) {
					return;
				}
				const id = locate(e);
				if (!id) {
					return;
				}
				this.data.remove(id);
			},
			ondragover: (e: DragEvent) => {
				const types = e.dataTransfer.types;
				for (const type of types) {
					if (type !== "Files" && type !== "application/x-moz-file") {
						return;
					}
				}
				if (this._dragoverTimeout) {
					clearTimeout(this._dragoverTimeout);
				} else {
					this.paint();
				}
				this._dragover = true;
				this._dragoverTimeout = setTimeout(() => {
					this._dragover = false;
					this._dragoverTimeout = null;
					this.paint();
				}, 150);
			},
			showHelper: (e) => {
				e.stopPropagation();
				e.preventDefault();
				this._helper.show(e.target);
			}
		};

		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	validate(): boolean {
		const isValid = !this.config.required || this.data.getLength() > 0;
		this.config.$validationStatus = isValid
			? ValidationStatus.success
			: ValidationStatus.error;
		this.paint();
		return isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this.paint();
	}
	clear(): void {
		this.data.removeAll();
		this.paint();
	}
	getValue(): any[] {
		return this.data.map(data => (data.file)) || [];
	}
	setConfig(config: ISimpleVault): void {
		if (isEmptyObj(config)) {
			return;
		}
		this.config = {
			type: this.config.type,
			id: this.config.id,
			name: this.config.name
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}
		this.events.fire(BaseElementEvent.configUpdate, [this.config]);
		this.paint();
	}
	getWidget(): Uploader {
		return this._uploader;
	}
	private _draw() {
		if (this.config.help) {
			if (this._helper) {
				this._helper.attachHTML(this.config.help);
			} else {
				this._helper = new Popup({css: "dhx_tooltip dhx_tooltip--forced dhx_tooltip--light"});
				this._helper.attachHTML(this.config.help);
			}
		}
		const files = this.data.getLength() ? el("ul.dhx_simplevault__files.dhx_simplevault-files",{
			class: this.config.$vaultHeight ? "" : "dhx_simplevault-files__fixed"},
			this.data.map(file => el("li.dhx_simplevault-files__item", [
				el("span.dhx_simplevault-files__item-name", file.file.name),
				el(".dhx_button.dhx_simplevault-files__delete.dhx_button--icon.dhx_button--view_link.dhx_button--size_small.dhx_button--color_secondary.dhx_button--circle", {
					dhx_id: file.id,
					onclick: this._handlers.remove,
				}, [
					el("span.dxi.dxi-delete-forever")
				])
			]))
		) : null;
		const { id, labelInline, label,	labelWidth,	help, disabled,	required, validation } = this.config;
		const width = labelInline && labelWidth ? labelWidth : "";

		return el(".dhx_form-group.dhx_form-group--simplevault", {
			class: getFormItemCss(this.config, Boolean(required) || Boolean(validation))
		}, [
			el("label.dhx_label", {
				for: id || this._uid,
				style: {minWidth: width, maxWidth: width},
				class: help ? "dhx_label--with-help" : "",
				onclick: this._handlers.add,
			}, help ? [
				el("span.dhx_label__holder", label),
				el("span.dhx_label-help.dxi.dxi-help-circle-outline", {
					tabindex: "0",
					role: "button",
					onclick: this._handlers.showHelper
				}),
			] : label),
			el(".dhx_input__wrapper", [
				el("div", {
					_hooks: {
						didInsert: (node: VNode) => {
							this._uploader.linkDropArea(node.el);
						}
					},
					ondragover: this._handlers.ondragover,
					class: "dhx_simplevault" + (this._dragover ? " dhx_simplevault--on-drag" : "")
				}, [
					el("div.dhx_simplevault-loader", [
					el("span.dhx_simplevault__icon.dxi.dxi-vault")
				]),
					el(".dhx_simplevault__drop-area", [
						el("input.dhx_simplevault__input", {
							type: "file",
							id,
							disabled,
						}),
						el("span.dhx_simplevault__icon.dxi.dxi-vault"),
						el("span.dhx_simplevault__title", [
							el("span", locale.simpleVaultText),
							el("br"),
							el("label.dhx_simplevault__label", {
								onclick: this._handlers.add,
								for: id
							}, " " + locale.simpleVaultLabel)
						]),
					]),
					files,
				]),
				getValidationMessage(this.config) && el("span.dhx_input__caption", {
				}, getValidationMessage(this.config))
			])
		]);
	}
}